/**
 * SearchPage — Résultats de recherche (fiches + clients)
 *
 * Reçoit le paramètre ?q= depuis la barre de recherche du Header.
 * Cherche en parallèle dans les fiches ET les clients.
 * Design aligné sur le site PHP (astro.arketyp.fr).
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import type { NodeSearchResult } from '../types/courses.js';
import type { Client, ClientsResponse } from '../types/clients.js';

type Tab = 'fiches' | 'clients';

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';

  const [fiches, setFiches] = useState<NodeSearchResult[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('fiches');

  useEffect(() => {
    if (!query || query.length < 2) {
      setFiches([]);
      setClients([]);
      return;
    }

    setIsLoading(true);
    setError('');

    Promise.all([
      apiFetch<NodeSearchResult[]>(`/nodes/search?q=${encodeURIComponent(query)}`),
      apiFetch<ClientsResponse>(`/clients?search=${encodeURIComponent(query)}&limit=50`),
    ])
      .then(([fichesRes, clientsRes]) => {
        if (fichesRes.success && fichesRes.data) {
          setFiches(fichesRes.data);
        }
        if (clientsRes.success && clientsRes.data) {
          setClients(clientsRes.data.clients);
        }
        // Auto-switch tab si un seul type a des résultats
        const hasFiches = fichesRes.data && fichesRes.data.length > 0;
        const hasClients = clientsRes.data && clientsRes.data.clients.length > 0;
        if (!hasFiches && hasClients) setTab('clients');
        else setTab('fiches');
      })
      .catch(() => setError('Erreur de connexion au serveur'))
      .finally(() => setIsLoading(false));
  }, [query]);

  function groupByParent(items: NodeSearchResult[]): Map<string, NodeSearchResult[]> {
    const groups = new Map<string, NodeSearchResult[]>();
    for (const item of items) {
      const name = item.parent?.nom ?? 'Racine';
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name)!.push(item);
    }
    return groups;
  }

  function formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  const grouped = groupByParent(fiches);
  const totalResults = fiches.length + clients.length;

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: 6,
    border: 'none',
    background: active ? colors.accent : 'transparent',
    color: active ? colors.text : colors.textMid,
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>
      {/* En-tête */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <i className="bi bi-search" style={{ fontSize: 20, color: colors.accent }} />
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 600, color: colors.text }}>
            Recherche
          </h1>
        </div>
        {query && (
          <p style={{ margin: 0, fontSize: 14, color: colors.textMid, marginLeft: 30 }}>
            {isLoading
              ? `Recherche de « ${query} »...`
              : `${totalResults} résultat${totalResults > 1 ? 's' : ''} pour « ${query} »`}
          </p>
        )}
      </div>

      {/* Pas de recherche */}
      {!query && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted }}>
          <i className="bi bi-search" style={{ fontSize: 40, opacity: 0.3, display: 'block', marginBottom: 12 }} />
          <div style={{ fontSize: 14 }}>Utilisez la barre de recherche pour trouver des fiches ou des clients.</div>
        </div>
      )}

      {/* Recherche trop courte */}
      {query && query.length < 2 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
          Entrez au moins 2 caractères pour lancer la recherche.
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 14,
            color: '#991b1b',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <i className="bi bi-exclamation-circle" />
          {error}
        </div>
      )}

      {/* Chargement */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
          Recherche en cours...
        </div>
      )}

      {/* Aucun résultat */}
      {!isLoading && query.length >= 2 && totalResults === 0 && !error && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted }}>
          <i className="bi bi-journal-text" style={{ fontSize: 40, opacity: 0.3, display: 'block', marginBottom: 12 }} />
          <div style={{ fontSize: 14 }}>Aucun résultat pour « {query} ».</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Essayez avec d'autres mots-clés.</div>
        </div>
      )}

      {/* Onglets + Résultats */}
      {!isLoading && totalResults > 0 && (
        <>
          {/* Onglets */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#f5f5f5', borderRadius: 8, padding: 4 }}>
            <button onClick={() => setTab('fiches')} style={tabStyle(tab === 'fiches')}>
              <i className="bi bi-file-text" style={{ fontSize: 13, marginRight: 6 }} />
              Fiches ({fiches.length})
            </button>
            <button onClick={() => setTab('clients')} style={tabStyle(tab === 'clients')}>
              <i className="bi bi-people" style={{ fontSize: 13, marginRight: 6 }} />
              Clients ({clients.length})
            </button>
          </div>

          {/* Tab Fiches */}
          {tab === 'fiches' && (
            fiches.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: colors.textMuted, fontSize: 14 }}>
                Aucune fiche trouvée.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                {Array.from(grouped.entries()).map(([themeName, items]) => (
                  <div key={themeName}>
                    <h2
                      style={{
                        margin: '0 0 12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                      }}
                    >
                      {themeName} ({items.length})
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {items.map((item) => (
                        <Link
                          key={item.id}
                          to={`/cours/${item.id}`}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 12,
                            padding: '16px 20px',
                            background: '#fff',
                            borderRadius: 10,
                            border: `1px solid ${colors.border}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            textDecoration: 'none',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = colors.accent;
                            e.currentTarget.style.boxShadow = `0 2px 8px ${colors.accent}1f`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                          }}
                        >
                          {item.symbole ? (
                            <span
                              style={{
                                fontFamily: "'Noto Sans Symbols 2', 'Segoe UI Symbol', sans-serif",
                                fontSize: 20,
                                color: colors.accent,
                                flexShrink: 0,
                                marginTop: 2,
                              }}
                            >
                              {item.symbole}
                            </span>
                          ) : (
                            <i className="bi bi-file-text" style={{ fontSize: 16, color: colors.textMuted, flexShrink: 0, marginTop: 3 }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text }}>
                              {item.nom}
                            </div>
                            {item.resume && (
                              <div
                                style={{
                                  fontSize: 13,
                                  color: colors.textMid,
                                  marginTop: 4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {item.resume}
                              </div>
                            )}
                            {item.element && (
                              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 6 }}>
                                {item.element}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Tab Clients */}
          {tab === 'clients' && (
            clients.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: colors.textMuted, fontSize: 14 }}>
                Aucun client trouvé.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {clients.map((client) => (
                  <Link
                    key={client.id}
                    to={`/clients/${client.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: '16px 20px',
                      background: '#fff',
                      borderRadius: 10,
                      border: `1px solid ${colors.border}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = colors.accent;
                      e.currentTarget.style.boxShadow = `0 2px 8px ${colors.accent}1f`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    }}
                  >
                    {/* Avatar */}
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: colors.accentLight,
                        color: '#8a6d0a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                    >
                      {client.prenom[0]}{client.nom[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text }}>
                        {client.prenom} {client.nom}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px', marginTop: 4, fontSize: 13, color: colors.textMid }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <i className="bi bi-calendar3" style={{ fontSize: 11 }} />
                          {formatDate(client.dateNaissance)}
                        </span>
                        {client.lieuNaissance && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-geo-alt" style={{ fontSize: 11 }} />
                            {client.lieuNaissance}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        background: colors.accentLight,
                        color: '#8a6d0a',
                        padding: '3px 10px',
                        borderRadius: 12,
                        flexShrink: 0,
                        fontWeight: 500,
                      }}
                    >
                      {client.charts.some((c) => c.type === 'NATAL') ? 'TN' : ''}{client.charts.some((c) => c.type === 'NATAL') && client.charts.some((c) => c.type === 'REVOLUTION_SOLAIRE') ? ' + ' : ''}{client.charts.some((c) => c.type === 'REVOLUTION_SOLAIRE') ? 'RS' : ''}
                    </span>
                    <i className="bi bi-chevron-right" style={{ fontSize: 12, color: '#ccc', flexShrink: 0 }} />
                  </Link>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
